import React, { useState, useEffect } from 'react';
import { getAllUsers, updateUserStatus, deleteUser } from '../../services/api';
import { 
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  UserCheck,
  UserX,
  Calendar,
  Clock,
  Mail,
  Building,
  ChevronDown,
  ChevronUp,
  Users,
  X
} from 'lucide-react';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterAndSearchUsers();
  }, [users, searchTerm, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSearchUsers = () => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const company = user.company || '';
        
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               email.toLowerCase().includes(searchTerm.toLowerCase()) ||
               company.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (filter !== 'all') {
      switch (filter) {
        case 'active':
          filtered = filtered.filter(user => user.subscription?.hasActiveAccess);
          break;
        case 'trial':
          filtered = filtered.filter(user => user.subscription?.plan === 'trial');
          break;
        case 'paid':
          filtered = filtered.filter(user => user.subscription?.plan === 'paid');
          break;
        case 'expired':
          filtered = filtered.filter(user => user.subscription?.plan === 'expired');
          break;
        case 'verified':
          filtered = filtered.filter(user => user.isVerified);
          break;
        case 'unverified':
          filtered = filtered.filter(user => !user.isVerified);
          break;
        default:
          break;
      }
    }

    setFilteredUsers(filtered);
  };

  const getSafeInitial = (user) => {
    if (user.name && user.name.trim()) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email && user.email.trim()) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getSafePlan = (user) => {
    return user.subscription?.plan || 'unknown';
  };

  const getSafeSubscriptionStatus = (user) => {
    if (!user.subscription) return { hasActiveAccess: false, isTrialActive: false };
    return {
      hasActiveAccess: user.subscription.hasActiveAccess || false,
      isTrialActive: user.subscription.isTrialActive || false,
      remainingTrialDays: user.subscription.remainingTrialDays || 0
    };
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await deleteUser(userId);
      if (response.success) {
        alert('✅ User deleted successfully!');
        fetchUsers();
      }
    } catch (error) {
      alert('❌ Error deleting user');
    }
  };

  const handleUpdateStatus = async (userId, status) => {
    try {
      const response = await updateUserStatus(userId, { isVerified: status });
      if (response.success) {
        alert('✅ User status updated!');
        fetchUsers();
      }
    } catch (error) {
      alert('❌ Error updating user status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getSubscriptionBadgeColor = (plan) => {
    if (plan === 'paid') return 'subscription-paid';
    if (plan === 'trial') return 'subscription-trial';
    if (plan === 'expired') return 'subscription-expired';
    return 'subscription-unknown';
  };

  const getStatusBadgeColor = (isVerified) => {
    return isVerified ? 'status-verified' : 'status-unverified';
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Plan', 'Status', 'Created At', 'Last Login'];
    const csvData = filteredUsers.map(user => [
      user.name || 'N/A',
      user.email || 'N/A',
      user.company || 'N/A',
      getSafePlan(user),
      user.isVerified ? 'Verified' : 'Unverified',
      formatDate(user.createdAt),
      formatDate(user.lastLogin)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="admin-users-loading">
        <div className="loading-spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      {/* Main Content - No Sidebar */}
      <div className="admin-users-main">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="top-bar-content">
            <h1 className="page-titles">User Management</h1>
            <div className="top-bar-actions">
              <div className="quick-stats">
                <span className="stat-itemss">
                  <span className="stat-labels">Total Users:</span>
                  <span className="stat-value">{users.length}</span>
                </span>
                <span className="stat-itemss">
                  <span className="stat-labels">Active:</span>
                  <span className="stat-value active">
                    {users.filter(u => u.subscription?.hasActiveAccess).length}
                  </span>
                </span>
                <span className="stat-itemss">
                  <span className="stat-labels">Paid:</span>
                  <span className="stat-value paid">
                    {users.filter(u => u.subscription?.plan === 'paid').length}
                  </span>
                </span>
              </div>
              
              <button onClick={exportToCSV} className="export-btn">
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="admin-users-content">
          {/* Search and Filter Bar */}
          <div className="control-bar">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Search users by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-container">
              <Filter className="filter-icon" size={20} />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Users</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="paid">Paid</option>
                <option value="expired">Expired</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-table-container">
            <div className="table-header">
              <h3 className="table-title">
                Users ({filteredUsers.length})
                <span className="table-subtitle">
                  Showing {filteredUsers.length} of {users.length} total users
                </span>
              </h3>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="empty-state">
                <UserX className="empty-icon" size={48} />
                <p className="empty-text">No users found</p>
                <p className="empty-subtext">
                  {searchTerm ? 'Try adjusting your search terms' : 'No users in the system yet'}
                </p>
              </div>
            ) : (
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Company</th>
                      <th>Subscription</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((userItem) => {
                      const subscriptionStatus = getSafeSubscriptionStatus(userItem);
                      const safeName = userItem.name || userItem.email || 'Unknown User';
                      const safeEmail = userItem.email || 'No email';
                      const safeCompany = userItem.company || 'No company';
                      const plan = getSafePlan(userItem);
                      
                      return (
                        <React.Fragment key={userItem._id}>
                          <tr className="user-row">
                            <td>
                              <div className="user-info-cell">
                                <div className={`user-avatar ${getSubscriptionBadgeColor(plan)}`}>
                                  {getSafeInitial(userItem)}
                                </div>
                                <div className="user-details">
                                  <div className="user-name">{safeName}</div>
                                  <div className="user-email">
                                    <Mail size={14} />
                                    {safeEmail}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="company-cell">
                                <Building size={16} />
                                {safeCompany}
                              </div>
                            </td>
                            <td>
                              <div className="subscription-cell">
                                <span className={`subscription-badge ${getSubscriptionBadgeColor(plan)}`}>
                                  {plan.toUpperCase()}
                                </span>
                                {userItem.subscription?.trialEndDate && subscriptionStatus.isTrialActive && (
                                  <div className="trial-info">
                                    <Clock size={12} />
                                    {subscriptionStatus.remainingTrialDays} days left
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${getStatusBadgeColor(userItem.isVerified)}`}>
                                {userItem.isVerified ? 'Verified' : 'Unverified'}
                              </span>
                            </td>
                            <td>
                              <div className="date-cell">
                                {formatDate(userItem.createdAt)}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={() => setExpandedUser(expandedUser === userItem._id ? null : userItem._id)}
                                  className="action-btn view-btn"
                                  title="View Details"
                                >
                                  {expandedUser === userItem._id ? <ChevronUp size={16} /> : <Eye size={16} />}
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(userItem._id, !userItem.isVerified)}
                                  className={`action-btn status-btn ${userItem.isVerified ? 'unverify' : 'verify'}`}
                                  title={userItem.isVerified ? 'Mark as Unverified' : 'Mark as Verified'}
                                >
                                  {userItem.isVerified ? <UserX size={16} /> : <UserCheck size={16} />}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(userItem._id)}
                                  className="action-btn delete-btn"
                                  title="Delete User"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Expanded Details */}
                          {expandedUser === userItem._id && (
                            <tr className="expanded-row">
                              <td colSpan="6">
                                <div className="expanded-content">
                                  <div className="expanded-section">
                                    <h4>Personal Information</h4>
                                    <div className="expanded-details">
                                      <div>
                                        <label>Full Name</label>
                                        <p>{safeName}</p>
                                      </div>
                                      <div>
                                        <label>Email</label>
                                        <p>{safeEmail}</p>
                                      </div>
                                      <div>
                                        <label>Company</label>
                                        <p>{safeCompany}</p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="expanded-section">
                                    <h4>Subscription Details</h4>
                                    <div className="expanded-details">
                                      <div>
                                        <label>Plan Type</label>
                                        <p>{userItem.subscription?.planType || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <label>Payment Status</label>
                                        <p>{userItem.subscription?.paymentStatus || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <label>Trial End</label>
                                        <p>{formatDate(userItem.subscription?.trialEndDate)}</p>
                                      </div>
                                      <div>
                                        <label>Subscription End</label>
                                        <p>{formatDate(userItem.subscription?.subscriptionEndDate)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="expanded-section">
                                    <h4>Account Details</h4>
                                    <div className="expanded-details">
                                      <div>
                                        <label>Account Created</label>
                                        <p>{formatDateTime(userItem.createdAt)}</p>
                                      </div>
                                      <div>
                                        <label>Last Login</label>
                                        <p>{formatDateTime(userItem.lastLogin)}</p>
                                      </div>
                                      <div>
                                        <label>Verification</label>
                                        <p className={userItem.isVerified ? 'verified' : 'unverified'}>
                                          {userItem.isVerified ? '✅ Verified' : '❌ Unverified'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="admin-users-footer">
            <div className="footer-content">
              <div className="footer-logo">HR Copilot Admin</div>
              <div className="footer-info">
                <span>Version 2.1.0</span>
                <span>•</span>
                <span>Total Users: {users.length}</span>
                <span>•</span>
                <span>Server: Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;